import { parse } from 'date-fns';
import { currentApiBaseUrl } from './environment';
import type { RatingSummary } from './types';

interface Rating {
  id: string;
  rating: number;
}

export async function getRatings(): Promise<Rating[]> {
  try {
    const url = new URL(`${currentApiBaseUrl}/ratings/?countryCodes=`);
    const response = await fetch(url.href, {
      headers: { 'Content-Type': 'application/json' },
    });
    return (await response.json()) as Rating[];
  } catch (err) {
    console.error('Failed to fetch ratings:', (err as Error).message);
    throw err;
  }
}

export async function getRatingSummary(dealerId: string): Promise<RatingSummary> {
  try {
    const url = new URL(`${currentApiBaseUrl}/rating-summary/`);
    url.searchParams.append('dealerId', dealerId);
    const response = await fetch(url.href, {
      headers: { 'Content-Type': 'application/json' },
    });
    return (await response.json()) as RatingSummary;
  } catch (err) {
    console.error('Failed to fetch rating summary:', (err as Error).message);
    throw err;
  }
}

interface ReviewApiResponse {
  id: string;
  categoryKey: string;
  rating: string;
  text: string;
  date: string;
  dealerResponse?: {
    text: string;
    date: string;
  } | null;
}

export interface Review {
  id: string;
  categoryKey: string;
  rating: number;
  text: string;
  date: Date;
  dealerResponse?: {
    text: string;
    date: Date;
  };
}

export async function getReviews(dealerId: string): Promise<Review[]> {
  try {
    const url = new URL(`${currentApiBaseUrl}/reviews/`);
    url.searchParams.append('dealerId', dealerId);

    const response = await fetch(url.href, {
      headers: { 'Content-Type': 'application/json' },
    });
    const data = (await response.json()) as ReviewApiResponse[];

    const referenceDate = new Date();

    return data.map((entry) => {
      const dealerResponse = entry.dealerResponse
        ? {
            text: entry.dealerResponse.text,
            date: parse(entry.dealerResponse.date, 'yyyy-MM-dd', referenceDate),
          }
        : null;

      return {
        id: entry.id,
        categoryKey: entry.categoryKey,
        rating: +entry.rating,
        text: entry.text,
        date: parse(entry.date, 'yyyy-MM-dd', referenceDate),
        ...(dealerResponse && { dealerResponse }),
      };
    });
  } catch (err) {
    console.warn('Failed to fetch reviews:', (err as Error).message);
    throw err;
  }
}

interface AuthService {
  fetch: (
    url: string,
    options: {
      resourceHostId: string;
      method: string;
      headers: Record<string, string>;
      body?: string;
    },
  ) => Promise<{ json: () => Promise<unknown> }>;
}

interface FavoritePartnerResponse {
  kvpsid?: string;
  tenant?: string;
}

export async function getFavoritePartner(
  authService: AuthService,
  locale: { language: string; countryCode: string },
): Promise<FavoritePartnerResponse> {
  try {
    const favPartner = await authService.fetch('patp/v1/sprmyaudi/v1/pas/v1/favorite-partner', {
      resourceHostId: 'retailservices',
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'Access-Control-Allow-Origin': '*',
        'Accept-Language': `${locale.language}-${locale.countryCode.toUpperCase()}`,
        'x-market': `${locale.language}_${locale.countryCode}`,
      },
    });
    return (await favPartner.json()) as FavoritePartnerResponse;
  } catch (e) {
    console.error('Error while gettin favorite partner.', { error: e });
    throw e;
  }
}

export async function setFavoritePartner(
  authService: AuthService,
  locale: { language: string; countryCode: string } | undefined,
  kvpsId: string | undefined,
  country: string | undefined,
): Promise<FavoritePartnerResponse> {
  try {
    const favPartner = await authService.fetch('patp/v1/sprmyaudi/v1/pas/v1/favorite-partner', {
      resourceHostId: 'retailservices',
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'Access-Control-Allow-Origin': '*',
        'Accept-Language': locale ? `${locale.language}-${locale.countryCode.toUpperCase()}` : '',
        'x-market': locale ? `${locale.language}_${locale.countryCode}` : '',
      },
      body: JSON.stringify({ tenant: country, kvpsid: kvpsId }),
    });
    return (await favPartner.json()) as FavoritePartnerResponse;
  } catch (e) {
    console.error('Error while setting new favorite partner.', { error: e });
    throw e;
  }
}
